import { TelemetryEvent, TelemetrySink, DataField, LogLevel, Category, logNotification, CustomerContentEvent } from '@microsoft/oteljs';
import { ApplicationInsights, IExtendedConfiguration } from './ApplicationInsights';
import { ARIA_OFFICE_TELEMETRY_TOKEN, tryCatch, CUSTOMER_CONTENT_TENANT_TOKEN } from './InternalUtils';
import { PartAPlugin } from './PartAPlugin';
import { IPlugin } from '@microsoft/1ds-core-js';
import { PerfManager } from './PerfManager';
import { OneDSSinkProperties, OneDSEndpoint } from './DataModels';
import { OneDSHelper } from './OneDSHelper';

// import { IPayloadData } from '@microsoft/1ds-post-js/dist-esm/src/DataModels';
// 1DS SDK post package version 2.1.4 does not export this :/

// Indirect type dependencies of OneDSSinkProperties
// TODO: Unfortunately the 1DS SDK does not follow the conventions define by the api-extractor tool causing it to fail, this making it
// impossible to check this in w/o pushing changes through 1DS SDK first :/
// export { INotificationListener, ITelemetryItem, IXHROverride, IPayloadData };

const voidValue: void = undefined;

/**
 * OTelJS 1DS Sink sends telemetry using the Web 1DS sdk
 */
export class OneDSSink extends OneDSHelper implements TelemetrySink {
  /**
   * Whether to use the Part A plugin (otherwise, fall back on appending data fields directly on the event)
   */
  protected usePartAPlugin = true;

  /**
   * Constructs an 1DS Sink
   * @param persistentDataFields - Data fields to be sent with every event; these data fields are assumed to
   * be top-level static fields, and will not be prepended with "Data."
   * @param oneDSSinkProperties - Properties on the sinks
   */
  constructor(persistentDataFields: DataField[], oneDSSinkProperties: OneDSSinkProperties) {
    super(persistentDataFields, oneDSSinkProperties);
    // #region Private variables
    /**
     * Data fields that are sent on every event.
     * This variable stores the data fields that have been set on the PartAPlugin
     */
    let _applicationInsights: ApplicationInsights | undefined;
    let _customerContentApplicationInsights: ApplicationInsights | undefined;
    let _partADataFieldsPlugin = new PartAPlugin();
    // #endregion

    // #region Private functions
    const hookUp1DSNotifications = (applicationInsights?: ApplicationInsights) => {
      if (applicationInsights) {
        applicationInsights.addNotificationListener({
          eventsSent: events => {
            logNotification(LogLevel.Info, Category.Transport, () => {
              return `Successfully sent ${events.length} event(s)`;
            });
            logNotification(LogLevel.Verbose, Category.Transport, () => {
              return `Sent event(s) details : ${JSON.stringify(events, null, 2)}`;
            });
            this.eventsSent += events.length;
          },
          eventsDiscarded: (events, reason: number) => {
            logNotification(LogLevel.Error, Category.Transport, () => {
              return `Discarded ${events.length} event(s) because ${reason}`;
            });
            logNotification(LogLevel.Verbose, Category.Transport, () => {
              return `Discarded event(s) details : ${JSON.stringify(events, null, 2)}`;
            });
            this.eventsDiscarded += events.length;
          }
        });
      }
    };
    // #endregion

    // #region Static private functions
    function getAppInsights(
      oneDSSinkProperties: OneDSSinkProperties,
      instrumentationKey: string,
      endpointUrl: string,
      plugins: IPlugin[]
    ): ApplicationInsights {
      const appInsightsConfig: IExtendedConfiguration = {
        instrumentationKey,
        endpointUrl,
        channelConfiguration: {
          eventsLimitInMem: oneDSSinkProperties.eventsLimitInMem,
          httpXHROverride: oneDSSinkProperties.httpXHROverride,
          setTimeoutOverride: oneDSSinkProperties.setTimeoutOverride,
          clearTimeoutOverride: oneDSSinkProperties.clearTimeoutOverride,
          ignoreMc1Ms0CookieProcessing: true,
          // 1ds-post-js in 3.1.x adds some optimization code that purportedly improves overall performance, but
          // in our testing regresses synchronous API performance. Disabling it here.
          disableOptimizeObj: true
        },
        disableCookiesUsage: true,
        extensionConfig: { ...oneDSSinkProperties.extensionConfig }
      };

      if (oneDSSinkProperties.stats && oneDSSinkProperties.stats.networkStats && appInsightsConfig.channelConfiguration) {
        appInsightsConfig.channelConfiguration.payloadListener = (originalPayload, modifiedPayload) => {
          const actualPayload = modifiedPayload ? modifiedPayload : originalPayload;
          if (actualPayload.data) {
            oneDSSinkProperties.stats?.networkStats(actualPayload.data.length);
          }
        };
      }

      const applicationInsights = new ApplicationInsights();
      applicationInsights.initialize(appInsightsConfig, plugins);
      applicationInsights.setUploadFrequency(oneDSSinkProperties.uploadFrequency);
      if (oneDSSinkProperties.notificationListener) {
        applicationInsights.addNotificationListener(oneDSSinkProperties.notificationListener);
      }
      if (oneDSSinkProperties.stats) {
        applicationInsights.setPerfMgr(new PerfManager(oneDSSinkProperties.stats));
      }
      return applicationInsights;
    }
    // #endregion

    // #region Public functions
    this.sendTelemetryEvent = (telemetryEvent: TelemetryEvent) => {
      return tryCatch(() => {
        const event = this.getOneDSTelemetryEvent(telemetryEvent);
        if (event && _applicationInsights) {
          _applicationInsights.track(event);
        }
      }, voidValue);
    };

    this.sendCustomerContent = (customerContentEvent: CustomerContentEvent) => {
      return tryCatch(() => {
        const event = this.getOneDSCustomerContent(customerContentEvent);
        if (event && _customerContentApplicationInsights) {
          _customerContentApplicationInsights.track(event);
        }
      }, voidValue);
    };

    /**
     * Note that this overrides the implementation in OneDSHelper
     */
    this.addPersistentDataFields = (dataFields: DataField[]) => {
      _partADataFieldsPlugin.addDataFields(dataFields);
    };

    this.flush = (async: boolean) => {
      if (_applicationInsights) {
        _applicationInsights.flush(async);
      }
    };

    this.shutdown = () => {
      try {
        if (_applicationInsights) {
          _applicationInsights.shutdown();
        }
      } catch (e) {
        logNotification(LogLevel.Error, Category.Transport, () => 'An error occurred on shutdown');
      }
    };
    // #endregion

    // #region Actual constructor stuff
    const plugins: IPlugin[] = [_partADataFieldsPlugin];
    if (oneDSSinkProperties.plugins) {
      plugins.push(...oneDSSinkProperties.plugins);
    }

    if (!oneDSSinkProperties.endpointUrl) {
      throw new Error('Missing Endpoint Url');
    }

    _applicationInsights = getAppInsights(oneDSSinkProperties, ARIA_OFFICE_TELEMETRY_TOKEN, oneDSSinkProperties.endpointUrl, plugins);
    if (oneDSSinkProperties.enableCustomerContent && oneDSSinkProperties.endpointUrl === OneDSEndpoint.PUBLIC) {
      _customerContentApplicationInsights = getAppInsights(
        oneDSSinkProperties,
        CUSTOMER_CONTENT_TENANT_TOKEN,
        OneDSEndpoint.CUSTOMER_CONTENT,
        plugins
      );
    }

    if (!oneDSSinkProperties.disableStatsTracking) {
      hookUp1DSNotifications(_applicationInsights);
      hookUp1DSNotifications(_customerContentApplicationInsights);
    }

    this.addPersistentDataFields(persistentDataFields);
    // #endregion
  }

  /**
   * Preprocess and send telemetry using the 1DS Sink
   * @param telemetryEvent - The telemetry event to be sent
   */
  sendTelemetryEvent: (telemetryEvent: TelemetryEvent) => void;

  /**
   * Preprocess and send customer content using the 1DS Sink
   * @param customerContentEvent - The telemetry event to be sent
   */
  sendCustomerContent: (customerContentEvent: CustomerContentEvent) => void;

  /**
   * Notifies the sink to flush the events.
   * @param async - Determines whether the operation is synchrounous or asynchrounous
   */
  flush: (async: boolean) => void;

  /**
   * Notifies the sink to send all outstanding events and shutdown.
   */
  shutdown: () => void;
}
